'use client';
import { useSubmitLead } from '@/features/lead-form/hooks/useSubmitLead';
import { useSystemInfo } from '@/features/lead-form/hooks/useSystemInfo';
import { LEAD_FORM_NAME, type LeadFormName } from '@/features/lead-form/constants/formName';
import type { LeadType } from '@/features/lead-form/constants/leadType';
import {
  buildItemName,
  omitEmptyList,
  pushLeadFormSubmit,
  type ItemCategory,
} from '@/features/lead-form/utils/leadFormGtm';
import type { LeadData, ProductType } from '@/features/lead-form/types/leadData';
import { formatPhoneNumberWithHyphen } from '@shared/utils/format';
import { useGtmUserId } from '@shared/hooks/useGtmUserId';
import { useLoginContactDefaults } from '@shared/hooks/useLoginContactDefaults';

import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useFormMode } from './_context';
import clsx from 'clsx';
import FormGroup from '@publishing/components/forms/formgroup';
import { Button } from '@publishing/components/ui/Button';
import AgreementBox from './common/AgreementBox';
import FieldRenderer from './common/FieldRenderer';
import FormTip from './common/FormTip';
import tipStyles from './common/FormTip.module.scss';
import type { FormField, FormAgreementOptions, SubmitAction, FormKey } from './_types';
import { resolveField, getFormFieldPresetKey, getRequiredAgreementIds } from './_types';
import { validateField } from './_validator';
import { FORM_MENUS, FORM_FIELD_ORDER } from './_config';
import SubmitResult from '@publishing/modules/submit/SubmitResult';
import styles from './Form.module.scss';

const DEFAULT_FIELDS: FormField[] = [
  { type: 'company' },
  { type: 'userCount' },
  { type: 'phone' },
  { type: 'email' },
];

type FormValues = Record<string, string>;
type SyncValues = Partial<Record<string, string | undefined>>;

function pruneFormRecord(record: FormValues, allowedKeys: Set<string>): FormValues {
  let changed = false;
  const next = { ...record };
  for (const key of Object.keys(next)) {
    if (!allowedKeys.has(key)) {
      delete next[key];
      changed = true;
    }
  }
  return changed ? next : record;
}

function applySyncValues(prev: FormValues, syncValues: SyncValues): FormValues {
  let changed = false;
  const next = { ...prev };

  for (const [name, value] of Object.entries(syncValues)) {
    if (value === undefined) {
      if (name in next && next[name] !== '') {
        delete next[name];
        changed = true;
      }
      continue;
    }
    if (next[name] !== value) {
      next[name] = value;
      changed = true;
    }
  }

  return changed ? next : prev;
}

function clearSyncedFieldErrors(prev: FormValues, syncValues: SyncValues): FormValues {
  let changed = false;
  const next = { ...prev };
  for (const name of Object.keys(syncValues)) {
    if (name in next) {
      delete next[name];
      changed = true;
    }
  }
  return changed ? next : prev;
}

// ─────────────────────────────────────────────────────────
// 프리셋 모드
// ─────────────────────────────────────────────────────────

/**
 * Form 사용 가이드
 *
 * ────────────────────────────────────────────────
 * 1. 프리셋 타입으로 사용 (기본)
 *    type에 FORM_KEY 값을 넘기면 필드·약관·팁이 자동 세팅됩니다.
 *
 *    <Form type={FORM_KEY.SHARED_OFFICE} />
 *
 * ────────────────────────────────────────────────
 * 2. 필드 추가 (프리셋 + 커스텀 필드 혼합)
 *    import { FIELDS_STANDARD } from '@publishing/modules/forms/_config';
 *
 *    <Form
 *      type={FORM_KEY.SHARED_OFFICE}
 *      fields={[
 *        ...FIELDS_STANDARD,
 *        { name: 'memo', label: '메모', type: 'text' },
 *      ]}
 *    />
 *
 * ────────────────────────────────────────────────
 * 3. 필드 완전 교체 (type 없이 fields만)
 *    type을 생략하고 fields만 넘기면 원하는 필드만 렌더됩니다.
 *
 *    <Form
 *      fields={[
 *        { name: 'phone', type: 'phone' },
 *        { name: 'email', type: 'email' },
 *      ]}
 *    />
 *
 * ────────────────────────────────────────────────
 * 4. submit 버튼 라벨 변경
 *
 *    <Form type={FORM_KEY.SHARED_OFFICE} submitLabel="무료 상담 신청" />
 *
 * ────────────────────────────────────────────────
 * 5. submit 후 이동할 페이지 지정
 *    문자열: 해당 경로로 router.push
 *    객체 type:'redirect': 동일
 *    객체 type:'link': window.open (external: true 시 새 탭)
 *    객체 type:'inline': 폼 영역에 결과 화면을 그 자리에 표시
 *
 *    <Form submitAction="/thank-you" />
 *    <Form submitAction={{ type: 'redirect', href: '/thank-you' }} />
 *    <Form submitAction={{ type: 'link', href: 'https://...', external: true }} />
 *    <Form submitAction={{ type: 'inline' }} />   // 페이지 이동 없이 결과 표시
 *
 *    ※ submitAction 미지정 시 기본값: 팝업이면 결과 없음, 페이지면 /submit 으로 이동
 *
 * ────────────────────────────────────────────────
 * 6. 마케팅 동의 필수 지정
 *    marketing: 'required'  → 마케팅 동의 필수 (미동의 시 submit 불가)
 *    marketing: 'optional'  → 선택 (기본값)
 *    marketing: 'hidden'    → 마케팅 항목 자체를 숨김
 *
 *    <Form agreement={{ marketing: 'required' }} />
 *
 * ────────────────────────────────────────────────
 * 7. submit 완료 콜백 (부모에서 후처리가 필요할 때)
 *    onSuccess를 넘기면 submitAction 대신 해당 콜백이 호출됩니다.
 *
 *    폼이 섹션 안에 있고, 제출 후 그 섹션 전체를 결과 화면으로 교체해야 할 때 사용합니다.
 *    결과 화면을 부모가 직접 제어해야 하는 경우(예: 폼 외부 상태 변경, 다른 UI 노출 등)에 적합합니다.
 *
 *    const [done, setDone] = useState(false);
 *    {done ? <InlineDefault /> : <Form onSuccess={() => setDone(true)} />}
 *
 *    formName 이 있으면 onSuccess 는 API(200) 성공 후에만 호출됩니다.
 *    새 API 는 응답 본문이 없어 인자가 없는 시그니처입니다.
 *
 *    ※ 단순히 폼 자리에 결과만 보여주면 된다면 onSuccess 대신
 *       submitAction={{ type: 'inline' }} 을 사용하세요. (부모 상태 관리 불필요)
 */
interface PresetFormProps {
  /** 필드 위(스크롤 영역 최상단)에 렌더할 콘텐츠 (폼 제목 등) */
  beforeFields?: ReactNode;
  /** 약관 영역 위에 렌더할 콘텐츠 (목적 체크박스 등) */
  beforeAgreement?: ReactNode;
  /** 사업 라인 분류 (다중 가능) — systemInfo.leadType. formName 과 함께 지정. */
  leadType?: LeadType[];
  /**
   * GA `item_category` 전용 오버라이드 — 미지정 시 `leadType` 을 그대로 쓴다.
   *
   * 상담 팝업처럼 선택한 상품에 따라 사업 종류가 달라지는 폼에서 쓴다.
   * `leadType` 은 Salesforce 라우팅 코드라 같이 바꾸면 리드 배분이 달라진다.
   */
  gtmItemCategory?: readonly ItemCategory[];
  /**
   * GA `item_category2` 전용 오버라이드 — 미지정 시 payload 의 `productType`.
   * `[]` 를 넘기면 스펙상 공란(`""`)으로 나간다.
   */
  gtmProductType?: readonly ProductType[];
  /** GA `location_id`·`item_name` 용 지점명 — 지점 컨텍스트가 있는 폼만. */
  gtmLocationName?: string;
  /** 한 페이지에 리드폼이 여럿일 때의 구분값(1-based) — 페이지가 직접 부여한다. */
  leadFormNum?: number;
  /** data.productType 으로 보낼 한글 상품 라벨 배열. 미지정 시 ['공유오피스']. */
  productType?: ProductType[];
  /** 프리셋 폼 타입. 미지정 시 DEFAULT_FIELDS(회사명·인원·연락처·이메일) 사용 */
  type?: FormKey;
  /** 렌더할 필드 목록. 지정 시 type의 기본 필드를 덮어씁니다 */
  fields?: FormField[];
  className?: string;
  style?: CSSProperties;
  actionAreaClassName?: string;
  /** 레이아웃 변형. 'main'이면 root gap을 0으로, 약관 박스 하단 여백을 띄움 */
  variant?: 'default' | 'main';
  /** 필드 그리드 컬럼 수. 기본 2열, 1이면 한 줄씩 */
  columns?: number;
  /** 약관 옵션. marketing: 'required' | 'optional'(기본) | 'hidden' */
  agreement?: FormAgreementOptions;
  /** submit 버튼 라벨. 미지정 시 type 설정값 → '문의하기' 순으로 폴백 */
  submitLabel?: string;
  /** submit 버튼 왼쪽 아이콘 (선택) */
  submitIcon?: ReactNode;
  /** 추가 유효성 조건 — false면 버튼 비활성화 */
  extraValid?: boolean;
  /**
   * submit 후 동작.
   * - 문자열: router.push(href)
   * - { type: 'redirect', href } : router.push
   * - { type: 'link', href, external? } : window.open
   * - { type: 'inline' } : 폼 자리에 결과 화면 표시
   * - 미지정: 팝업이면 아무것도 안 함 / 페이지면 /submit 으로 이동
   */
  submitAction?: SubmitAction | string;
  /** 필드 하단 팁 노출 여부 (기본 true) */
  showTip?: boolean;
  /** 초기값 */
  initialValues?: Record<string, string>;
  /** 외부 상태(상품 선택 등)에 따라 특정 필드 값 동기화. undefined 값이면 해당 필드 비움 */
  syncValues?: Partial<Record<string, string | undefined>>;
  /** submit 시 data 에 추가로 병합할 variant 별 필드 (downloadPurpose, remark 등). */
  extraData?: Record<string, unknown>;
  /** 값이 바뀔 때마다 호출 */
  onValuesChange?: (values: Record<string, string>) => void;
  /** submit 시 호출 (formName 미지정 케이스). 지정 시 submitAction/onSuccess 보다 우선. */
  onSubmit?: (values: Record<string, string>, agreements: string[]) => void;
  /**
   * 제출 성공 후 콜백 — 지정 시 submitAction 대신 실행.
   * 새 API 는 응답 본문 없음 — 인자 없음.
   */
  onSuccess?: () => void;
  /** API 제출 실패 시 호출 (네트워크·4xx/5xx 등). formName 미지정 시 미사용. */
  onSubmitError?: (error: unknown) => void;
  /** SF systemInfo.formName 값. 기본값 'N_통합상담문의' */
  formName?: LeadFormName;
  children?: ReactNode;
}

function PresetForm({
  beforeFields,
  beforeAgreement,
  leadType,
  gtmItemCategory,
  gtmProductType,
  gtmLocationName,
  leadFormNum,
  productType,
  type,
  fields: fieldsProp,
  className,
  style,
  actionAreaClassName,
  variant = 'default',
  columns,
  agreement,
  submitLabel: submitLabelProp,
  submitIcon,
  extraValid = true,
  submitAction,
  showTip = true,
  initialValues,
  syncValues,
  extraData,
  onValuesChange,
  onSubmit: onSubmitProp,
  onSuccess,
  onSubmitError,
  formName = LEAD_FORM_NAME.INTEGRATED_CONSULT,
  children,
}: PresetFormProps) {

  const { mutate: submitLead, isPending } = useSubmitLead();
  const gtmUserId = useGtmUserId();
  // useSystemInfo 는 hook 이라 무조건 호출 — formName 미지정 시 placeholder 로 placeholder 빌드만.
  const { build: buildSystemInfo } = useSystemInfo({
    formName: formName ?? LEAD_FORM_NAME.INTEGRATED_CONSULT,
    leadType: leadType ?? [],
  });

  const router = useRouter();
  const formMode = useFormMode();
  const [showComplete, setShowComplete] = useState(false);

  const menuConfig = useMemo(
    () => (type ? FORM_MENUS.find((m) => m.key === type) : undefined),
    [type],
  );

  const fields = fieldsProp ?? menuConfig?.fields ?? DEFAULT_FIELDS;
  const submitLabel = submitLabelProp ?? menuConfig?.submitLabel ?? '문의하기';

  const marketingConsent = agreement?.marketing ?? menuConfig?.agreement?.marketing ?? 'optional';
  const agreementLang = agreement?.lang ?? menuConfig?.agreement?.lang ?? 'ko';
  const requiredIds = useMemo(() => getRequiredAgreementIds(marketingConsent), [marketingConsent]);

  const resolvedFields = useMemo(() => fields.map(resolveField), [fields]);

  const activeFieldNames = useMemo(
    () => new Set(resolvedFields.map((field) => field.name)),
    [resolvedFields],
  );

  const {
    phone: loginPhone,
    email: loginEmail,
    name: loginName,
  } = useLoginContactDefaults();

  const [agreedValues, setAgreedValues] = useState<Record<string, boolean>>({});

  const [values, setValues] = useState<Record<string, string>>(() => {
    const defaults = Object.fromEntries(
      resolvedFields
        .filter((f) => f.defaultValue !== undefined)
        .map((f) => [f.name, f.defaultValue as string]),
    );
    return { ...defaults, ...initialValues };
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [agreementsSatisfied, setAgreementsSatisfied] = useState(true);

  // fields에서 빠진 항목(예: 희망면적) 값·에러 state 정리 — 기존 필드 값은 유지
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect -- activeFieldNames 변경 시 stale key 제거 */
    setValues((prev) => pruneFormRecord(prev, activeFieldNames));
    setErrors((prev) => pruneFormRecord(prev, activeFieldNames));
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [activeFieldNames]);

  // 로그인 상태면 성함·휴대전화·이메일을 채워 준다.
  // `/client/me` 응답이 마운트보다 늦게 오므로 초기 state 가 아니라 effect 에서 반영하고,
  // 이미 입력된 값은 덮지 않는다 (응답이 늦게 도착해 입력 중이던 내용을 지우면 안 된다).
  useEffect(() => {
    const contact: SyncValues = {};
    if (loginPhone) contact.phone = loginPhone;
    if (loginEmail) contact.email = loginEmail;
    // `companyName` 은 '회사명 또는 성함' 필드 — 이 폼을 안 쓰는 구성이면 키를 넣지 않는다.
    if (loginName && activeFieldNames.has('companyName')) {
      contact.companyName = loginName;
    }
    if (Object.keys(contact).length === 0) return;

    /* eslint-disable react-hooks/set-state-in-effect -- 로그인 정보 도착 시 빈 필드만 채운다 */
    setValues((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const [name, value] of Object.entries(contact)) {
        if (next[name] || !value) continue;
        next[name] = value;
        changed = true;
      }
      return changed ? next : prev;
    });
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [loginPhone, loginEmail, loginName, activeFieldNames]);

  // 외부 syncValues(userCount 등) — 필드 prune 과 분리해 사용자 입력값 유지
  useEffect(() => {
    if (!syncValues) return;
    /* eslint-disable react-hooks/set-state-in-effect -- syncValues prop 변경 시 해당 필드만 반영 */
    setValues((prev) => applySyncValues(prev, syncValues));
    setErrors((prev) => clearSyncedFieldErrors(prev, syncValues));
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [syncValues]);

  useEffect(() => {
    onValuesChange?.(values);
  }, [values, onValuesChange]);

  const setFieldValue = useCallback((name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const handleAgreedChange = useCallback(
    (agreed: Record<string, boolean>) => {
      setAgreedValues(agreed);
      setAgreementsSatisfied(requiredIds.every((id) => Boolean(agreed[id])));
    },
    [requiredIds],
  );

  const isFormValid = useMemo(() => {
    if (!agreementsSatisfied) return false;
    if (!extraValid) return false;
    return resolvedFields.every((field) => {
      const value = values[field.name] ?? '';
      return validateField({
        fieldType: field.type as string,
        value,
        values,
        required: field.required,
        label: field.label,
        requiredMessage: field.requiredMessage,
        presetKey: getFormFieldPresetKey(field),
        customValidator: field.validate ? (v) => field.validate!(v, values) : undefined,
      }) === null;
    });
  }, [resolvedFields, values, agreementsSatisfied, extraValid]);

  const handleSubmit = useCallback(() => {
    setSubmitAttempted(true);

    const nextErrors: Record<string, string> = {};
    resolvedFields.forEach((field) => {
      const value = values[field.name] ?? '';
      const error = validateField({
        fieldType: field.type as string,
        value,
        values,
        required: field.required,
        label: field.label,
        requiredMessage: field.requiredMessage,
        presetKey: getFormFieldPresetKey(field),
        customValidator: field.validate ? (v) => field.validate!(v, values) : undefined,
      });
      if (error) nextErrors[field.name] = error;
    });
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0 || !agreementsSatisfied || !extraValid) return;

    const afterLeadSuccess = () => {
      if (onSubmitProp) {
        onSubmitProp(values, requiredIds);
        return;
      }
      if (onSuccess) {
        onSuccess();
      } else {
        applySubmitAction(resolveAction(submitAction, formMode), router, () => setShowComplete(true));
      }
    };

    if (formName) {
      // 공통 data 조립 — values/agreedValues 의 알려진 키 매핑, 그 외 extraData spread.
      const commonName = values.companyName ?? values.company ?? values.name ?? '';
      const userCountNum = values.userCount ? Number(values.userCount.replace(/,/g, '')) : 0;
      const hasOfficeAreaField = activeFieldNames.has('officeArea');
      const officeAreaNum = hasOfficeAreaField && values.officeArea
        ? Number(values.officeArea.replace(/,/g, ''))
        : 0;
      const data = {
        productType: productType ?? ['공유오피스'],
        phone: formatPhoneNumberWithHyphen(values.phone ?? ''),
        email: values.email ?? '',
        name: formName === LEAD_FORM_NAME.AFFILIATE ? `(파트너) ${commonName}` : commonName,
        company: formName === LEAD_FORM_NAME.AFFILIATE ? `(파트너) ${commonName}` : commonName,
        isPrivacy: agreedValues.isPrivacy === true,
        isMarketing: agreedValues.isMarketing === true,
        ...(userCountNum > 0 ? { userCount: userCountNum } : {}),
        ...(officeAreaNum > 0 ? { officeArea: officeAreaNum } : {}),
        ...(values.remark ? { remark: values.remark } : {}),
        ...(values.needsEtc ? { needsEtc: values.needsEtc } : {}),
        ...extraData,
      } as LeadData;

      const systemInfo = buildSystemInfo();
      submitLead(
        { body: { systemInfo, data } },
        {
          onSuccess: () => {
            pushLeadFormSubmit({
              btnName: submitLabel,
              formName,
              leadFormNum,
              // 오버라이드는 그대로(공란 의도 보존), 폴백 값은 빈 배열이면 키를 뺀다.
              leadType: gtmItemCategory ?? omitEmptyList(leadType),
              productType: gtmProductType ?? omitEmptyList(data.productType),
              itemName: buildItemName(gtmLocationName, data.productType),
              locationId: gtmLocationName,
              email: data.email,
              phone: data.phone,
              headcount: userCountNum > 0 ? userCountNum : undefined,
              desk: userCountNum > 0 ? userCountNum : undefined,
              py: officeAreaNum > 0 ? officeAreaNum : undefined,
              userId: gtmUserId,
            });
            afterLeadSuccess();
          },
          onError: (error) => {
            onSubmitError?.(error);
          },
        },
      );
      return;
    }

    if (onSubmitProp) {
      onSubmitProp(values, requiredIds);
      return;
    }

    if (onSuccess) {
      onSuccess();
    } else {
      applySubmitAction(resolveAction(submitAction, formMode), router, () => setShowComplete(true));
    }
  }, [
    resolvedFields,
    values,
    agreementsSatisfied,
    extraValid,
    requiredIds,
    onSubmitProp,
    onSuccess,
    onSubmitError,
    submitAction,
    formMode,
    router,
    formName,
    productType,
    submitLead,
    buildSystemInfo,
    agreedValues,
    extraData,
    activeFieldNames,
    leadType,
    gtmItemCategory,
    gtmProductType,
    gtmLocationName,
    leadFormNum,
    submitLabel,
    gtmUserId,
  ]);

  if (showComplete) {
    return <SubmitResult type="page" headingLevel={2} />;
  }

  return (
    <form
      className={clsx(styles.root, variant === 'main' && styles.main, className)}
      style={style}
      noValidate
      onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
    >
      <div className={styles.form_scroll}>
        {beforeFields}

        <FormGroup columns={columns}>
          {resolvedFields.map((field) => (
            <div key={field.name} style={{ order: FORM_FIELD_ORDER[field.name] ?? 999 }}>
              <FieldRenderer
                field={field}
                value={values[field.name] ?? ''}
                errorMessage={errors[field.name]}
                values={values}
                onChange={(v) => setFieldValue(field.name, v)}
                className={styles.field_item}
              />
            </div>
          ))}
        </FormGroup>

        {type && (
          <FormTip
            formKey={type}
            values={values}
            enabled={showTip}
            className={tipStyles.narrow}
          />
        )}

        {beforeAgreement}

        <AgreementBox
          lang={agreementLang}
          required={requiredIds}
          onAgreedChange={handleAgreedChange}
          forceShowError={submitAttempted && !agreementsSatisfied}
          variant={variant}
        />

        {children ? <div className={styles.children_slot}>{children}</div> : null}
      </div>

      <div className={clsx(styles.action_area, actionAreaClassName)}>
        <Button
          type="black"
          theme="solid"
          size="large"
          onClick={handleSubmit}
          disabled={!isFormValid || isPending}
          className={styles.submit_button}
          icon={false}
        >
          {submitIcon}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────
// 공용 유틸
// ─────────────────────────────────────────────────────────

function resolveAction(
  submitAction: SubmitAction | string | undefined,
  mode: 'popup' | 'page',
): SubmitAction | undefined {
  if (submitAction) {
    return typeof submitAction === 'string'
      ? { type: 'redirect', href: submitAction }
      : submitAction;
  }
  if (mode === 'popup') return undefined;
  return { type: 'redirect', href: '/submit' };
}

function applySubmitAction(
  action: SubmitAction | undefined,
  router: ReturnType<typeof useRouter>,
  onInline?: () => void,
) {
  if (!action) return;
  if (action.type === 'inline') {
    onInline?.();
  } else if (action.type === 'redirect') {
    router.push(action.href);
  } else if (action.type === 'link') {
    window.open(action.href, action.external ? '_blank' : '_self');
  }
}

// ─────────────────────────────────────────────────────────
// 진입점 — module 먼저 분기
// ─────────────────────────────────────────────────────────

export type FormProps = { type?: FormKey } & PresetFormProps;

export default function Form(props: FormProps) {
  return <PresetForm {...props} />;
}

export { FORM_KEY, resolveFormActionFormName } from './_types';
export type { SubmitAction, FormField, FormKey, FormAgreementOptions, FormActionFormName } from './_types';
export type { LeadFormName } from '@/features/lead-form/constants/formName';
